/**
 * Granola settings card — connect/replace/remove the org's Granola API key.
 * The key is write-only; the server returns just `configured` + lastFour.
 */
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { granola, granolaAdmin } from "../../lib/api";
import { Button, Input, toast } from "@/ui";

const CONFIG_KEY = ["loop", "granola-config"] as const;

export default function GranolaSettings() {
    const queryClient = useQueryClient();
    const [key, setKey] = useState("");
    const configQuery = useQuery({ queryKey: CONFIG_KEY, queryFn: () => granola.config() });
    const config = configQuery.data?.ok ? configQuery.data : null;

    const save = useMutation({
        mutationFn: async () => {
            const res = await granola.setKey(key.trim());
            if (!res.ok) throw new Error(res.error);
        },
        onSuccess: () => {
            setKey("");
            toast.success("Granola connected.");
            queryClient.invalidateQueries({ queryKey: CONFIG_KEY });
        },
        onError: (err: unknown) => toast.error(err instanceof Error ? err.message : "Couldn't save the key"),
    });
    const clear = useMutation({
        mutationFn: () => granola.clearKey(),
        onSuccess: () => {
            toast.success("Granola key removed.");
            queryClient.invalidateQueries({ queryKey: CONFIG_KEY });
        },
    });
    const relinkAll = useMutation({
        mutationFn: async () => {
            const res = await granolaAdmin.relinkAll(90);
            if (!res.ok) throw new Error(res.error);
            return res;
        },
        onSuccess: (res) => {
            toast.success(`Linked ${res.linked} of ${res.scannedMeetings} unlinked meetings (${res.scannedNotes} notes scanned).`);
            queryClient.invalidateQueries({ queryKey: ["loop"] });
        },
        onError: (err: unknown) => toast.error(err instanceof Error ? err.message : "Relink failed"),
    });

    return (
        <section style={{ marginTop: 32 }}>
            <h3>Granola</h3>
            <p className="muted" style={{ fontSize: 13, marginTop: 4 }}>
                Link Granola meeting notes and transcripts to your CRM meetings. Get an API
                key from the Granola desktop app under Settings → API.
            </p>
            {config?.configured ? (
                <div className="row" style={{ gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                    <span className="chip">Connected · key ····{config.lastFour}</span>
                    <Button size="sm" disabled={relinkAll.isPending}
                        title="Auto-link every unlinked meeting from the last 90 days to its exact Granola match"
                        onClick={() => relinkAll.mutate()}>
                        {relinkAll.isPending ? "Matching…" : "Relink recent meetings"}
                    </Button>
                    <Button size="sm" variant="danger" disabled={clear.isPending} onClick={() => clear.mutate()}>
                        Remove key
                    </Button>
                </div>
            ) : (
                <div className="row" style={{ gap: 8, alignItems: "center", maxWidth: 480 }}>
                    <Input
                        type="password"
                        placeholder="Granola API key"
                        value={key}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setKey(e.target.value)}
                    />
                    <Button disabled={key.trim().length < 8 || save.isPending} onClick={() => save.mutate()}>
                        {save.isPending ? "Checking…" : "Connect"}
                    </Button>
                </div>
            )}
        </section>
    );
}
